import { cva, type VariantProps } from 'class-variance-authority'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/cn'
/* The tone maps live in lib/statusTone: the board tints its lanes from the same
   table this pill colours itself from, and one table is what keeps the two
   saying the same thing. */
import { CASE_TONES, QUOTE_TONES } from '@/lib/statusTone'
import type { CaseStatus, QuoteStatus } from '@/types/domain'

/* The status pill replaces status TEXT in cells. Roundness is what makes a
 * status readable as a shape rather than as text, which is the single change
 * that most removes the spreadsheet feeling from a dense table.
 *
 * Never render a status as bare uppercase text in a cell — `FINANCIALLY CLOSED`
 * is eighteen characters that must be READ; a pill is a shape that is
 * RECOGNISED. And colour never carries meaning alone: every pill pairs its tone
 * with a label and a 5px dot, because roughly one man in twelve has a colour
 * vision deficiency and this is a finance product where a misread sign is
 * expensive. */

const pill = cva(
  'inline-flex items-center gap-1.5 rounded-full px-[9px] py-[3px] ' +
    'text-[11px] leading-none font-semibold whitespace-nowrap',
  {
    variants: {
      tone: {
        // Brand tint is the IN-PROGRESS state only. Blue is deliberately not
        // the colour of success, information, or selection-in-general.
        brand: 'bg-brand-soft text-brand',
        positive: 'bg-positive-soft text-positive',
        negative: 'bg-negative-soft text-negative',
        attention: 'bg-attention-soft text-attention',
        neutral: 'bg-neutral-100 text-text-muted',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
)

export interface StatusPillProps extends VariantProps<typeof pill> {
  children: React.ReactNode
  className?: string
  /** Hide the leading dot where the pill is already unambiguous (e.g. a delta). */
  dot?: boolean
}

/* Base UI (`@base-ui/react`): no primitive. A status pill is a static label; `cva` earns its place
   here because the pill genuinely has one variant per lifecycle state. */
export function StatusPill({
  tone,
  children,
  className,
  dot = true,
}: StatusPillProps) {
  return (
    <span className={cn(pill({ tone }), className)}>
      {dot ? (
        <span
          className="size-[5px] shrink-0 rounded-full bg-current"
          aria-hidden
        />
      ) : null}
      {children}
    </span>
  )
}

export function CaseStatusPill({ status }: { status: CaseStatus }) {
  const { t } = useTranslation()
  return <StatusPill tone={CASE_TONES[status]}>{t(`caseStatus.${status}`)}</StatusPill>
}

export function QuoteStatusPill({ status }: { status: QuoteStatus }) {
  const { t } = useTranslation()
  return <StatusPill tone={QUOTE_TONES[status]}>{t(`quoteStatus.${status}`)}</StatusPill>
}
